/**
 * Format a number to a string based on provided parameters with enhanced visual formatting
 */
export function formatNumericValue(
  value: number,
  { 
    formatValue, 
    decimalPlaces,
    prefix = "",
    suffix = ""
  }: { 
    formatValue?: (value: number) => string, 
    decimalPlaces?: number,
    prefix?: string,
    suffix?: string
  }
): string {
  let formattedValue: string;
  
  if (formatValue) {
    formattedValue = formatValue(value);
  } else if (typeof decimalPlaces === 'number') {
    formattedValue = value.toFixed(decimalPlaces);
  } else {
    // Improved number formatting with thousand separators
    formattedValue = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  }
  
  // Add prefix and suffix if they aren't already included
  if (!formattedValue.includes(suffix) && suffix) {
    formattedValue = `${formattedValue}${suffix}`;
  }
  
  if (!formattedValue.includes(prefix) && prefix) {
    formattedValue = `${prefix}${formattedValue}`;
  }
  
  return formattedValue;
}

/**
 * Attempt to parse a string into a number, handling various formats
 */
export function parseNumericInput(
  input: string,
  {
    parseValue,
    prefix = "",
    suffix = "",
    allowNegative = false
  }: {
    parseValue?: (input: string) => number,
    prefix?: string,
    suffix?: string,
    allowNegative?: boolean
  }
): number {
  try {
    let parsedValue: number;
    
    if (parseValue) {
      parsedValue = parseValue(input);
    } else {
      // Detect if input is just a simple number without formatting
      const isSimpleNumber = /^-?\d+\.?\d*$/.test(input);
      
      if (isSimpleNumber) {
        parsedValue = parseFloat(input);
      } else {
        // Enhanced parsing - remove prefix, suffix and non-numeric chars except decimal point and negative
        const cleaned = input
          .replace(prefix, '')
          .replace(suffix, '')
          .replace(/[^\d.-]/g, '')
          .replace(/,/g, ''); // Remove thousand separators
        
        // Handle multiple decimal points by keeping only the first one
        const firstDecimalIndex = cleaned.indexOf('.');
        let sanitized = cleaned;
        
        if (firstDecimalIndex !== -1) {
          const beforeDecimal = cleaned.substring(0, firstDecimalIndex + 1);
          const afterDecimal = cleaned.substring(firstDecimalIndex + 1).replace(/\./g, '');
          sanitized = beforeDecimal + afterDecimal;
        }
        
        // Better handling of empty or incomplete inputs
        parsedValue = sanitized === '' || sanitized === '-' || sanitized === '.' ? 0 : parseFloat(sanitized);
      }
    }
    
    if (isNaN(parsedValue)) {
      return 0;
    }
    
    // Handle negative values if not allowed
    if (!allowNegative && parsedValue < 0) {
      return 0;
    }
    
    return parsedValue;
  } catch (error) {
    console.error("Error parsing numeric input:", error);
    return 0;
  }
}

/**
 * Clamp a number value between min and max
 */
export function clampValue(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Format number with animated transitions
 * For use with framer-motion or CSS transitions
 */
export function getTransitionValues(value: number): { value: number } {
  return { value };
}

/**
 * Get appropriate styling based on value thresholds
 */
export function getValueStyling(value: number, threshold?: number): string {
  if (!threshold) return "text-foreground";
  
  if (value > threshold * 1.1) {
    return "text-homebuyer-danger font-medium";
  } else if (value > threshold) {
    return "text-homebuyer-warning font-medium";
  } else {
    return "text-homebuyer-success font-medium";
  }
}
